if (!customElements.get("ashpveda-frequently-bought")) {
  customElements.define(
    "ashpveda-frequently-bought",
    class AshpvedaFrequentlyBought extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll("[data-fbt-item]"));

        this.addButton = this.querySelector("[data-fbt-add]");
        this.buttonText = this.querySelector("[data-fbt-button-text]");
        this.spinner = this.querySelector("[data-fbt-spinner]");
        this.message = this.querySelector("[data-fbt-message]");

        this.totalElement = this.querySelector("[data-fbt-total]");
        this.compareTotalElement = this.querySelector(
          "[data-fbt-total-compare]",
        );

        this.currency = this.dataset.currency || "INR";
        this.defaultButtonText =
          this.dataset.buttonText || "Add selected to cart";

        this.formatter = new Intl.NumberFormat(
          document.documentElement.lang || "en-IN",
          {
            style: "currency",
            currency: this.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          },
        );

        this.onComponentChange = this.onComponentChange.bind(this);

        this.onDocumentChange = this.onDocumentChange.bind(this);

        this.addSelectedProducts = this.addSelectedProducts.bind(this);

        this.addEventListener("change", this.onComponentChange);

        this.addButton?.addEventListener("click", this.addSelectedProducts);

        /*
         * The currently selected PDP variant is synchronized
         * with the current-product card inside the bundle.
         */
        document.addEventListener("change", this.onDocumentChange, true);

        this.mainVariantInput = document.querySelector(
          '.wt-product__wrapper product-form form[data-type="add-to-cart-form"] input[name="id"]',
        );

        this.items.forEach((item) => {
          this.updateItem(item);
        });

        this.syncCurrentProductVariant();
        this.updateTotals();
      }

      disconnectedCallback() {
        this.removeEventListener("change", this.onComponentChange);

        this.addButton?.removeEventListener("click", this.addSelectedProducts);

        document.removeEventListener("change", this.onDocumentChange, true);
      }

      formatMoney(cents) {
        const amount = Number(cents || 0) / 100;
        return this.formatter.format(amount);
      }

      onComponentChange(event) {
        const item = event.target.closest("[data-fbt-item]");

        if (item) {
          this.updateItem(item);
        }

        this.clearMessage();
        this.updateTotals();
      }

      onDocumentChange(event) {
        if (this.contains(event.target)) return;

        /*
         * Wait until the theme updates its hidden variant ID.
         */
        window.setTimeout(() => {
          this.syncCurrentProductVariant();
          this.updateTotals();
        }, 50);
      }

      syncCurrentProductVariant() {
        const currentItem = this.querySelector('[data-current-product="true"]');

        if (!currentItem) return;

        this.mainVariantInput =
          document.querySelector(
            '.wt-product__wrapper product-form form[data-type="add-to-cart-form"] input[name="id"]',
          ) || this.mainVariantInput;

        const selectedVariantId = this.mainVariantInput?.value;

        if (!selectedVariantId) return;

        const currentSelect = currentItem.querySelector("[data-fbt-variant]");

        if (!currentSelect) return;

        const matchingOption = Array.from(currentSelect.options).find(
          (option) => String(option.value) === String(selectedVariantId),
        );

        if (!matchingOption || matchingOption.disabled) return;

        currentSelect.value = selectedVariantId;
        this.updateItem(currentItem);
      }

      updateItem(item) {
        const select = item.querySelector("[data-fbt-variant]");
        const checkbox = item.querySelector("[data-fbt-checkbox]");

        if (!select || !checkbox) return;

        const option = select.selectedOptions[0];

        if (!option) return;

        const price = Number(option.dataset.price || 0);
        const comparePrice = Number(option.dataset.comparePrice || 0);

        const isAvailable = option.dataset.available === "true";

        item.dataset.variantId = option.value;
        item.dataset.price = String(price);
        item.dataset.comparePrice = String(comparePrice);

        checkbox.disabled = !isAvailable;

        if (!isAvailable) {
          checkbox.checked = false;
        }

        const currentPrice = item.querySelector("[data-fbt-current-price]");

        const comparePriceElement = item.querySelector(
          "[data-fbt-compare-price]",
        );

        if (currentPrice) {
          currentPrice.textContent = this.formatMoney(price);
        }

        if (comparePriceElement) {
          const hasDiscount = comparePrice > price;

          comparePriceElement.hidden = !hasDiscount;
          comparePriceElement.textContent = hasDiscount
            ? this.formatMoney(comparePrice)
            : "";
        }

        const imageUrl = option.dataset.image;
        const image = item.querySelector("[data-fbt-image]");

        if (imageUrl && image) {
          image.removeAttribute("srcset");
          image.src = imageUrl;
        }
      }

      getSelectedItems() {
        return this.items.filter((item) => {
          const checkbox = item.querySelector("[data-fbt-checkbox]");

          return checkbox?.checked && !checkbox.disabled;
        });
      }

      updateTotals() {
        const selectedItems = this.getSelectedItems();

        let total = 0;
        let compareTotal = 0;

        selectedItems.forEach((item) => {
          const price = Number(item.dataset.price || 0);
          const comparePrice = Number(item.dataset.comparePrice || 0);

          total += price;
          compareTotal += comparePrice > price ? comparePrice : price;
        });

        if (this.totalElement) {
          this.totalElement.textContent = this.formatMoney(total);
        }

        if (this.compareTotalElement) {
          const hasCombinedDiscount = compareTotal > total;

          this.compareTotalElement.hidden = !hasCombinedDiscount;

          this.compareTotalElement.textContent = hasCombinedDiscount
            ? this.formatMoney(compareTotal)
            : "";
        }

        if (this.addButton) {
          this.addButton.disabled = selectedItems.length === 0;
        }
      }

      setLoading(isLoading) {
        if (!this.addButton) return;

        this.addButton.disabled = isLoading;
        this.addButton.classList.toggle("is-loading", isLoading);

        if (this.spinner) {
          this.spinner.hidden = !isLoading;
        }

        if (this.buttonText) {
          this.buttonText.textContent = isLoading
            ? "Adding..."
            : this.defaultButtonText;
        }

        if (!isLoading) {
          this.updateTotals();
        }
      }

      clearMessage() {
        if (!this.message) return;

        this.message.textContent = "";
        this.message.classList.remove("is-error");
      }

      showMessage(text, isError = false) {
        if (!this.message) return;

        this.message.textContent = text;
        this.message.classList.toggle("is-error", isError);
      }

      async addSelectedProducts() {
        const selectedItems = this.getSelectedItems();

        if (!selectedItems.length) {
          this.showMessage("Select at least one product.", true);
          return;
        }

        const items = selectedItems.map((item) => ({
          id: Number(item.dataset.variantId),
          quantity: 1,
        }));

        const cartDrawer = document.querySelector("cart-drawer");

        const cartSections =
          typeof cartDrawer?.getSectionsToRender === "function"
            ? cartDrawer
                .getSectionsToRender()
                .map((section) => section.id)
                .filter(Boolean)
            : [];

        const payload = {
          items,
        };

        if (cartSections.length) {
          payload.sections = cartSections;
          payload.sections_url = window.location.pathname;
        }

        const root = window.Shopify?.routes?.root || "/";

        const cartAddUrl = window.routes?.cart_add_url || `${root}cart/add.js`;

        this.clearMessage();
        this.setLoading(true);

        try {
          cartDrawer?.setActiveElement?.(document.activeElement);

          const response = await fetch(cartAddUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(payload),
          });

          const result = await response.json();

          if (!response.ok || result.status || result.errors) {
            throw new Error(
              result.description ||
                result.message ||
                "Unable to add the selected products.",
            );
          }

          if (
            typeof publish === "function" &&
            typeof PUB_SUB_EVENTS !== "undefined" &&
            PUB_SUB_EVENTS.cartUpdate
          ) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "ashpveda-frequently-bought",
              cartData: result,
            });
          }

          const cartType = cartDrawer?.dataset?.cartType;

          if (
            cartDrawer &&
            cartType === "drawer" &&
            result.sections &&
            typeof cartDrawer.renderContents === "function"
          ) {
            const isClosedCart = !document.body.classList.contains(
              "page-overlay-cart-on",
            );

            cartDrawer.renderContents(result, isClosedCart);

            this.showMessage("Selected products added to your cart.");
          } else {
            window.location.assign(window.routes?.cart_url || `${root}cart`);
          }
        } catch (error) {
          console.error("Frequently Bought Together error:", error);

          this.showMessage(
            error.message || "Something went wrong. Please try again.",
            true,
          );
        } finally {
          this.setLoading(false);
        }
      }
    },
  );
}
